import Editor, { useMonaco } from '@monaco-editor/react';
import { useObservable } from '../utils/UseObservable';
import { currentResult, isDecompiling } from '../logic/Decompiler';
import { useEffect, useRef, useState } from 'react';
import { editor, Range } from "monaco-editor";
import { isDarkMode, isThin } from '../logic/Browser';
import { classesList } from '../logic/JarFile';
import { CodeTab, getOpenTab } from '../logic/tabs';
import { message, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { getTokenLocation } from '../logic/Tokens';
import { getNextJumpToken, nextReferenceNavigation } from '../logic/FindAllReferences';
import { setupJavaBytecodeLanguage } from '../utils/JavaBytecode';
import { applyJavadocCodeExtensions } from '../javadoc/JavadocCodeExtensions';
import { ENABLE_JAVADOC_EDITOR } from '../javadoc/JavadocConfig';
import { selectedInheritanceClassName } from '../logic/Inheritance';
import { createHoverProvider } from './CodeHoverProvider';
import { findTokenAtPosition } from './CodeUtils';
import {
    IS_DEFINITION_CONTEXT_KEY_NAME,
    createCopyAwAction,
    createCopyAtAction,
    createCopyMixinAction,
    createFindAllReferencesAction,
    createViewInheritanceAction
} from './CodeContextActions';
import {
    clearTokenJump,
    createDefinitionProvider,
    createEditorOpener,
    createFoldingRangeProvider,
    jumpToToken,
    pendingTokenJump
} from './CodeExtensions';
import { bytecode } from '../logic/Settings';
import { selectedFile, diffView, openTabs, selectedLines, tabHistory, referencesQuery, mobileDrawerOpen } from '../logic/State';
import { toClassFilePath } from '../utils/Names';

const IS_ANDROID_CHROME = /Android/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent);

const Code = () => {
    const monaco = useMonaco();

    const decompileResult = useObservable(currentResult);
    const classList = useObservable(classesList);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const hideMinimap = useObservable(isThin);
    const darkMode = useObservable(isDarkMode);
    const decompiling = useObservable(isDecompiling);
    const selectedLine = useObservable(selectedLines);
    const nextReference = useObservable(nextReferenceNavigation);
    const tokenJump = useObservable(pendingTokenJump);

    const decorationsCollectionRef = useRef<editor.IEditorDecorationsCollection | null>(null);
    const lineHighlightRef = useRef<editor.IEditorDecorationsCollection | null>(null);
    const decompileResultRef = useRef(decompileResult);
    const classListRef = useRef(classList);

    const [messageApi, contextHolder] = message.useMessage();

    const [resetViewTrigger, setResetViewTrigger] = useState(false);

    function applyTokenDecorations(model: editor.ITextModel) {
        if (!decompileResult) return;

        // Reapply token decorations for the current tab
        if (editorRef.current && decompileResult.tokens) {
            const decorations = decompileResult.tokens.map(token => {
                const startPos = model.getPositionAt(token.start);
                const endPos = model.getPositionAt(token.start + token.length);
                const canGoTo = !token.declaration && classList && classList.includes(toClassFilePath(token.className));

                return {
                    range: new Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
                    options: {
                        inlineClassName: token.type + '-token-decoration' + (canGoTo ? "-pointer" : "")
                    }
                };
            });

            decorationsCollectionRef.current?.clear();
            decorationsCollectionRef.current = editorRef.current.createDecorationsCollection(decorations);
        }
    }

    // Keep refs updated
    useEffect(() => {
        decompileResultRef.current = decompileResult;
        classListRef.current = classList;
    }, [decompileResult, classList]);

    useEffect(() => {
        if (!monaco) return;
        monaco.editor.setTheme(darkMode ? "vs-dark" : "vs");
    }, [monaco, darkMode]);

    useEffect(() => {
        if (!monaco) return;
        if (!editorRef.current) return;
        const editor = editorRef.current;

        const definitionProvider = monaco.languages.registerDefinitionProvider(
            "java",
            createDefinitionProvider(decompileResultRef, classListRef)
        );

        const hoverProvider = monaco.languages.registerHoverProvider(
            "java",
            createHoverProvider(editorRef, decompileResultRef, classListRef)
        );

        const editorOpener = monaco.editor.registerEditorOpener(
            createEditorOpener(decompileResultRef)
        );

        const foldingRange = monaco.languages.registerFoldingRangeProvider(
            "java",
            createFoldingRangeProvider(monaco)
        );

        const copyAw = monaco.editor.addEditorAction(
            createCopyAwAction(decompileResultRef, classListRef, messageApi)
        );

        const copyAt = monaco.editor.addEditorAction(
            createCopyAtAction(decompileResultRef, classListRef, messageApi)
        );

        const copyMixin = monaco.editor.addEditorAction(
            createCopyMixinAction(decompileResultRef, classListRef, messageApi)
        );

        const viewAllReferences = monaco.editor.addEditorAction(
            createFindAllReferencesAction(decompileResultRef, classListRef, messageApi, (value) => {
                mobileDrawerOpen.next(true);
                referencesQuery.next(value);
            })
        );

        const viewInheritance = monaco.editor.addEditorAction(
            createViewInheritanceAction(decompileResultRef, classListRef, messageApi, (value) => selectedInheritanceClassName.next(value))
        );

        const bytecode = setupJavaBytecodeLanguage(monaco);

        return () => {
            // Dispose in the oppsite order
            bytecode.dispose();
            viewInheritance.dispose();
            viewAllReferences.dispose();
            copyMixin.dispose();
            copyAt.dispose();
            copyAw.dispose();
            foldingRange.dispose();
            editorOpener.dispose();
            hoverProvider.dispose();
            definitionProvider.dispose();
        };
    }, [monaco, decompileResult, classList, resetViewTrigger, messageApi]);

    if (ENABLE_JAVADOC_EDITOR) {
        useEffect(() => {
            if (!monaco || !editorRef.current || !decompileResult) return;

            const extensions = applyJavadocCodeExtensions(monaco, editorRef.current, decompileResult);

            return () => {
                extensions.dispose();
            };
            // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
        }, [monaco, editorRef.current, decompileResult]);
    }

    // Scroll to top when source changes, or to specific line if specified
    useEffect(() => {
        if (editorRef.current && decompileResult) {
            const editor = editorRef.current;
            lineHighlightRef.current?.clear();

            const executeScroll = () => {
                const currentLine = selectedLine?.line;
                if (currentLine) {
                    const lineEnd = selectedLine?.lineEnd ?? currentLine;
                    editor.setSelection(new Range(currentLine, 1, currentLine, 1));
                    editor.revealLinesInCenterIfOutsideViewport(currentLine, lineEnd);

                    // Highlight the line range
                    lineHighlightRef.current = editor.createDecorationsCollection([{
                        range: new Range(currentLine, 1, lineEnd, 1),
                        options: {
                            isWholeLine: true,
                            className: 'highlighted-line',
                            glyphMarginClassName: 'highlighted-line-glyph'
                        }
                    }]);
                }
            };

            // Use requestAnimationFrame to ensure Monaco has finished layout
            requestAnimationFrame(() => {
                executeScroll();
            });
        }
    }, [decompileResult, selectedLine]);

    // Scroll to a "Find All References" token
    useEffect(() => {
        if (editorRef.current && decompileResult) {
            if (decompileResult.language !== "java") return;

            const editor = editorRef.current;

            lineHighlightRef.current?.clear();

            const executeScroll = () => {
                const nextJumpToken = getNextJumpToken(decompileResult);
                const nextJumpLocation = nextJumpToken && getTokenLocation(decompileResult, nextJumpToken);

                if (nextJumpLocation) {
                    const { line, column, length } = nextJumpLocation;
                    editor.revealLinesInCenterIfOutsideViewport(line, line);
                    editor.setSelection(new Range(line, column, line, column + length));
                }
            };

            requestAnimationFrame(() => {
                executeScroll();
            });
        }
    }, [decompileResult, nextReference]);

    // Subscribe to tab changes and store model & viewstate of previously opened tab
    useEffect(() => {
        // Cache if diffview is opened and restore if it is closed;
        const sub = diffView.subscribe((open) => {
            const openTab = getOpenTab();
            if (!(openTab instanceof CodeTab)) return;
            if (open) {
                openTab.onBlur();
            } else {
                if (!openTab) return;
                selectedFile.next(openTab.key);

                // While this is not perfect, it works because leaving the diff view
                // makes the view invisible and doesn't apply any of the custom "extensions",
                // manually forcing a rerender works ^-^
                setTimeout(() => {
                    setResetViewTrigger(!resetViewTrigger);
                }, 100);
            }
        });

        return () => {
            sub.unsubscribe();
        };
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    // Handles setting the model and viewstate of the editor
    useEffect(() => {
        if (diffView.value) return;
        if (!monaco || !decompileResult) return;

        const tab = getOpenTab();
        if (!tab || !(tab instanceof CodeTab)) return;

        // This fixes the following problem:
        // new tab opens -> setModel is set from old decompileResult.source
        // -> view is invalidated -> viewstate is lost
        // so this is quite important to keep!
        if (!tab.key.includes(decompileResult.className)) return;

        tab.editorRef = editorRef.current;

        // Set new model with the current decompilation source
        tab.setModel(monaco.editor.createModel(
            decompileResult.source,
            bytecode.value ? "bytecode" : "java",
            monaco.Uri.parse(`inmemory://${Date.now()}`)
        ));

        // Only restore view state if there's no line to jump to
        // Otherwise the line highlighting effect will handle scrolling
        if (editorRef.current) {
            if (!selectedLine) {
                tab.applyViewToEditor(editorRef.current);
            } else {
                // Just set the model without restoring view state
                if (tab.model) {
                    editorRef.current.setModel(tab.model);
                }
            }
        }
        applyTokenDecorations(tab.model!);
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [decompileResult, resetViewTrigger, selectedLine, monaco]);

    // Process pending token jumps after model is loaded
    useEffect(() => {
        if (!editorRef.current || !decompileResult || !tokenJump) return;

        if (toClassFilePath(decompileResult.className) === tokenJump.className) {
            requestAnimationFrame(() => {
                if (editorRef.current && decompileResult) {
                    jumpToToken(decompileResult, tokenJump.targetType, tokenJump.target, editorRef.current);
                    clearTokenJump();
                }
            });
        }
    }, [decompileResult, tokenJump]);

    // Handle gutter clicks for line linking
    useEffect(() => {
        if (!editorRef.current) return;
        const codeEditor = editorRef.current;

        const onMouseDown = codeEditor.onMouseDown((e) => {
            if (e.target.type === editor.MouseTargetType.GUTTER_LINE_NUMBERS ||
                e.target.type === editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
                const lineNumber = e.target.position?.lineNumber;

                if (lineNumber) {
                    // Shift-click to select a range
                    console.log(selectedLine);
                    if (e.event.shiftKey && selectedLine) {
                        selectedLines.next({ line: selectedLine.line, lineEnd: lineNumber });
                    } else {
                        selectedLines.next({ line: lineNumber });
                    }
                }
            }
        });

        return () => {
            onMouseDown.dispose();
        };
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [editorRef.current, selectedLine]);

    return (
        <Spin
            indicator={<LoadingOutlined spin />}
            size={"large"}
            spinning={!!decompiling}
            description="Decompiling..."
            styles={{
                root: {
                    height: '100%',
                    color: 'white'
                },
                container: {
                    height: '100%',
                }
            }}
        >
            {contextHolder}
            <Editor
                defaultLanguage={"java"}
                language={decompileResult?.language}
                theme={darkMode ? "vs-dark" : "vs"}
                options={{
                    readOnly: true,
                    domReadOnly: true,
                    tabSize: 3,
                    minimap: { enabled: !hideMinimap },
                    glyphMargin: true,
                    foldingImportsByDefault: true,
                    foldingHighlight: false,
                    scrollBeyondLastLine: false,
                    editContext: IS_ANDROID_CHROME ? false : undefined, // Disable content editable on Android Chrome to attempt to stop the virtual keyboard from appearing
                }}
                onMount={(codeEditor) => {
                    editorRef.current = codeEditor;

                    // Update context key when cursor position changes
                    // We use this to know when to show the options to copy AW/Mixin strings
                    const isDefinitionContextKey = codeEditor.createContextKey<boolean>(IS_DEFINITION_CONTEXT_KEY_NAME, false);
                    codeEditor.onDidChangeCursorPosition((e) => {
                        const token = findTokenAtPosition(codeEditor, decompileResultRef.current, classListRef.current);
                        const validToken = token != null && (token.type == "class" || token.type == "method" || token.type == "field");
                        isDefinitionContextKey.set(validToken);
                    });
                }} />
        </Spin>
    );
};

export default Code;
